import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { computed, toValue } from 'vue'

import { DATE_UNITS, ELEMENT_PERIODS_DATE_FORMAT } from '@/utils/date'
import { resolveBinaryProgressValue } from '@/utils/element-progress'
import { memoizeFormatNumber } from '@/utils/memoizations'
import { isKPI } from '@/utils/objectives'
import {
  DEFAULT_BINARY_TARGET_VALUE,
  DEFAULT_CURRENT_VALUE,
  DEFAULT_ORIGINAL_VALUE,
  DEFAULT_TARGET_VALUE,
  getConditionParameterById,
  isBinaryProgress,
  isStartTargetProgress,
  isThresholdProgress,
  METRIC_VALUES_KEYS
} from '@/utils/okr-element-values'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

dayjs.extend(isBetween)

export const useMetricType = ({ element = {} } = {}) => {
  const isBinary = computed(() => isBinaryProgress({ element: toValue(element) }))

  const isStartTarget = computed(() => isStartTargetProgress({ element: toValue(element) }))

  const isThreshold = computed(() => isThresholdProgress({ element: toValue(element) }))

  const currentMetricIsKPI = computed(() => isKPI(toValue(element)))

  return {
    isBinary,
    isStartTarget,
    isThreshold,
    currentMetricIsKPI
  }
}

export const useMetricValues = ({ element = {} } = {}) => {
  const { isBinary } = useMetricType({ element })

  const getOriginalValue = computed(() => {
    return toValue(element)[METRIC_VALUES_KEYS.ORIGINAL_VALUE] ?? DEFAULT_ORIGINAL_VALUE // after link jira-issue in autoMode we get null from backend
  })

  const getTargetValue = computed(() => {
    if (isBinary.value) return DEFAULT_BINARY_TARGET_VALUE

    return toValue(element)[METRIC_VALUES_KEYS.TARGET_VALUE] ?? DEFAULT_TARGET_VALUE
  })

  const getBinaryValue = computed(() => {
    return resolveBinaryProgressValue(toValue(element)[METRIC_VALUES_KEYS.CURRENT_VALUE])
  })

  const getCurrentValue = computed(() => {
    const value = toValue(element)[METRIC_VALUES_KEYS.CURRENT_VALUE] ?? DEFAULT_CURRENT_VALUE
    return `${memoizeFormatNumber({ value })} ${unit.value}`.trim()
  })

  const unit = computed(() => {
    return toValue(element)[OKR_ELEMENT_KEYS.UNIT] || ''
  })

  const getThresholdPrefix = computed(() => {
    return getConditionParameterById({
      conditionId: toValue(element)[METRIC_VALUES_KEYS.THRESHOLD_CONDITION_TYPE_ID],
      parameter: 'symbol'
    })
  })

  return {
    getOriginalValue,
    getTargetValue,
    getBinaryValue,
    getCurrentValue,
    unit,
    getThresholdPrefix
  }
}

export const useKPI = ({ element = {}, selectedDate = null } = {}) => {
  const { currentMetricIsKPI } = useMetricType({ element })
  const { getTargetValue } = useMetricValues({ element })

  const getRelevantThresholdTargetValue = computed(() => {
    if (!currentMetricIsKPI.value) return getTargetValue.value

    const { elementTargetPeriods = [] } = toValue(element)

    const normalizedSelectedDate = dayjs(toValue(selectedDate) ?? dayjs()).format(
      ELEMENT_PERIODS_DATE_FORMAT
    )

    const relevantPeriod = elementTargetPeriods.find(period => {
      const { startDate, endDate } = period

      return dayjs(normalizedSelectedDate).isBetween(startDate, endDate, DATE_UNITS.DAY, '[]')
    })

    return relevantPeriod ? relevantPeriod[METRIC_VALUES_KEYS.TARGET_VALUE] : getTargetValue.value
  })

  return {
    getRelevantThresholdTargetValue
  }
}
